import { ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useSnackBar } from "@/utils/useSnackbar";
import { useAppDispatch } from "@/redux/hooks";
import { updatePersonInInbox } from "@/pages/Private/redux/list/list.slice";
import { ListPerson } from "@/pages/Private/pages/List/schema/list";

import {
	usePauseLeadOutreachMutation,
	useResumeLeadOutreachMutation,
} from "@/pages/Private/redux/list/list-relationship.api";
import {
	useContactAddToListMutation,
	useContactAddToTrackerMutation,
} from "@/pages/Private/redux/contact/contact.api";
import { useAddProspectsToCrmMutation } from "@/pages/Private/redux/crm/crm.api";
import {
	CrmEntityType,
	CrmProvider,
	CrmSelectionSource,
} from "@/pages/Private/redux/crm/crm.schema";
import { AddToCrmListOption } from "@/pages/Private/components/AddToCrm/addToCrmListOptions";
import { AddToCrmOwner } from "@/pages/Private/components/AddToCrm/AddToCrmModal";

import { ContactSelectType } from "../../schema/contacts";

import { isHoldableCampaignStep } from "./outreachHoldState";
import { ComposeReachOutApi, useComposeReachOut } from "./useComposeReachOut";

interface UseLeadPanelActionsArgs {
	prospectId: number;
	prospectName: string;
	/** The lead's resolved `ListPerson` id — the panel's own row. */
	listPersonId?: number | null;
	/**
	 * The panel's own row, when the host has it. Pausing and resuming change `stopCampaignAt`, which
	 * is what the action row reads to decide "paused" vs "in a campaign" — and on every list surface
	 * that row is served from the `inbox` slice (`UnassignedTable` passes `inbox.find(...)` down).
	 * Mirroring the new cut-off there is what makes the panel flip without a reload; RTK cache
	 * invalidation cannot stand in for it, because the row-listing queries that fill `inbox` carry
	 * the bare `"ListPerson"` tag, which an id-scoped invalidation does not match.
	 */
	listPerson?: ListPerson | null;
	/** Receives the `ListPerson` created by the Compose-list reach-out (PREC-3825). */
	onComposeListPersonCreated?: (listPersonId: number) => void;
}

interface AddToListTarget {
	listId?: number;
	listName?: string;
}

export interface LeadPanelActionsApi extends ComposeReachOutApi {
	addToList: (target: AddToListTarget) => Promise<void>;
	isAddingToList: boolean;
	addToTracker: (trackerListId: number) => Promise<void>;
	isAddingToTracker: boolean;
	addToCrm: (
		provider: CrmProvider,
		entities: CrmEntityType[],
		owner?: AddToCrmOwner,
		list?: AddToCrmListOption
	) => Promise<void>;
	isAddingToCrm: boolean;
	/**
	 * Stops the campaign on `listPersonId` — which the caller must name, because the row being
	 * paused is not always the row the panel is showing (PREC-3906).
	 */
	pauseCampaign: (listPersonId: number) => Promise<void>;
	isPausing: boolean;
	/** Releases the campaign {@link pauseCampaign} held on `listPersonId`. */
	resumeCampaign: (listPersonId: number) => Promise<void>;
	isResuming: boolean;
	renderSnackbar: () => ReactNode;
}

/**
 * Centralises the lead-panel action-row mutations that act directly on a prospect id (add-to-list /
 * -tracker / -CRM) plus the per-person outreach pause. Each handler shows a success/error toast.
 * The list, campaign and webhook *pickers* own their own commit paths; this hook only handles the
 * one-shot commits.
 */
export const useLeadPanelActions = ({
	prospectId,
	prospectName,
	listPersonId,
	listPerson,
	onComposeListPersonCreated,
}: UseLeadPanelActionsArgs): LeadPanelActionsApi => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	/**
	 * Mirror a pause/resume onto the panel's row in the inbox slice. Scoped to that row on purpose:
	 * the only campaign these buttons ever act on is this one — a campaign running on another list
	 * is reported, never paused from here — so a differing id means the row on screen did not change.
	 *
	 * **Both of the server's writes, or neither.** `pause-outreach` sets the cut-off *and* holds the
	 * lead's outstanding campaign steps, and the panel reads the steps first (they are the half every
	 * surface actually carries — `list-person-info.dto.ts` serves no `stopCampaignAt`). Mirroring only
	 * the cut-off left the Outbox showing "Pause outreach" over a lead that was already paused in the
	 * database, and clicking it again did nothing visible (PREC-4107).
	 */
	const applyOutreachHold = useCallback(
		(targetListPersonId: number, stopCampaignAt: string | null) => {
			if (!listPerson || listPerson.id !== targetListPersonId) {
				return;
			}

			const held = stopCampaignAt !== null;

			dispatch(
				updatePersonInInbox({
					personId: targetListPersonId,
					updatedPerson: {
						...listPerson,
						stopCampaignAt,
						schedules: listPerson.schedules?.map((schedule) =>
							isHoldableCampaignStep(schedule) ? { ...schedule, active: !held } : schedule
						),
					},
				})
			);
		},
		[dispatch, listPerson]
	);

	const notifyError = useCallback(
		() => handleOpenSnackbar(t("basics.error")),
		[handleOpenSnackbar, t]
	);
	const { startComposeReachOut, isComposing } = useComposeReachOut({
		prospectId,
		onListPersonCreated: onComposeListPersonCreated,
		onError: notifyError,
	});

	const [addToListMutation, { isLoading: isAddingToList }] = useContactAddToListMutation();
	const [addToTrackerMutation, { isLoading: isAddingToTracker }] = useContactAddToTrackerMutation();
	const [addToCrmMutation, { isLoading: isAddingToCrm }] = useAddProspectsToCrmMutation();
	const [pauseOutreach, { isLoading: isPausing }] = usePauseLeadOutreachMutation();
	const [resumeOutreach, { isLoading: isResuming }] = useResumeLeadOutreachMutation();

	const addToList = useCallback(
		async ({ listId, listName }: AddToListTarget) => {
			try {
				await addToListMutation({
					selectType: ContactSelectType.CURRENT_PAGE,
					ids: [prospectId],
					listId,
					listName,
				}).unwrap();
				handleOpenSnackbar(
					t("contacts.leadPanel.actions.toast.addedToList", { name: prospectName })
				);
			} catch {
				handleOpenSnackbar(t("basics.error"));
			}
		},
		[addToListMutation, prospectId, prospectName, handleOpenSnackbar, t]
	);

	const addToTracker = useCallback(
		async (trackerListId: number) => {
			try {
				await addToTrackerMutation({
					selectType: ContactSelectType.CURRENT_PAGE,
					ids: [prospectId],
					listId: trackerListId,
				}).unwrap();
				handleOpenSnackbar(
					t("contacts.leadPanel.actions.toast.addedToTracker", { name: prospectName })
				);
			} catch {
				handleOpenSnackbar(t("basics.error"));
			}
		},
		[addToTrackerMutation, prospectId, prospectName, handleOpenSnackbar, t]
	);

	/**
	 * A list-scoped selection when the caller named a membership, the prospect-scoped one
	 * otherwise.
	 *
	 * The distinction is not cosmetic: a CONTACTS selection reaches the provider sync with no
	 * list context at all (`CrmAddService.resolveContactsSelection` sends `listId: 0`), which
	 * leaves the `list.name` / `campaign.name` field mappings unset and gives the Dynamics
	 * marketing sync nothing to name its marketing list after.
	 */
	const buildSelection = useCallback(
		(list?: AddToCrmListOption) =>
			list
				? {
						source: CrmSelectionSource.LIST,
						selectType: "CURRENT_PAGE" as const,
						listId: list.listId,
						listPersonIds: [list.listPersonId],
					}
				: {
						source: CrmSelectionSource.CONTACTS,
						selectType: "CURRENT_PAGE" as const,
						ids: [prospectId],
					},
		[prospectId]
	);

	const addToCrm = useCallback(
		async (
			provider: CrmProvider,
			entities: CrmEntityType[],
			owner?: AddToCrmOwner,
			list?: AddToCrmListOption
		) => {
			try {
				await addToCrmMutation({
					provider,
					entities,
					ownerId: owner?.id,
					ownerName: owner?.name,
					selection: buildSelection(list),
				}).unwrap();
				handleOpenSnackbar(
					t("contacts.leadPanel.actions.toast.addedToCrm", { name: prospectName })
				);
			} catch {
				handleOpenSnackbar(t("basics.error"));
			}
		},
		[addToCrmMutation, buildSelection, prospectName, handleOpenSnackbar, t]
	);

	const pauseCampaign = useCallback(
		async (targetListPersonId: number) => {
			try {
				const { stopCampaignAt } = await pauseOutreach({
					listPersonId: targetListPersonId,
					panelListPersonId: listPersonId ?? undefined,
				}).unwrap();

				applyOutreachHold(targetListPersonId, stopCampaignAt);
				handleOpenSnackbar(
					t("contacts.leadPanel.actions.toast.campaignPaused", { name: prospectName })
				);
			} catch {
				handleOpenSnackbar(t("basics.error"));
			}
		},
		[pauseOutreach, listPersonId, applyOutreachHold, prospectName, handleOpenSnackbar, t]
	);

	const resumeCampaign = useCallback(
		async (targetListPersonId: number) => {
			try {
				await resumeOutreach({
					listPersonId: targetListPersonId,
					panelListPersonId: listPersonId ?? undefined,
				}).unwrap();

				applyOutreachHold(targetListPersonId, null);
				handleOpenSnackbar(
					t("contacts.leadPanel.actions.toast.campaignResumed", { name: prospectName })
				);
			} catch {
				handleOpenSnackbar(t("basics.error"));
			}
		},
		[resumeOutreach, listPersonId, applyOutreachHold, prospectName, handleOpenSnackbar, t]
	);

	return {
		startComposeReachOut,
		isComposing,
		addToList,
		isAddingToList,
		addToTracker,
		isAddingToTracker,
		addToCrm,
		isAddingToCrm,
		pauseCampaign,
		isPausing,
		resumeCampaign,
		isResuming,
		renderSnackbar: renderMessageSnackbar,
	};
};
